import { dashboardObservability } from "../utils/observability";
import { defineEvent as defineOperationalEvent } from "@diverge/observability/browser";
const operationalLogger = dashboardObservability.logger("dashboard/context/useAuthBootstrap");
const operationalEvents = {
  event1: defineOperationalEvent({
    name: "dashboard.context.use_auth_bootstrap.profile_bootstrap_failed.bcbd35c5",
    message: "Profile bootstrap failed:",
    level: "error",
    attributes: { "di.reason_code": { type: "category" } },
    classification: "operational",
    volume: "boundary",
    owner: "dashboard/src/context/useAuthBootstrap",
  }),
  event2: defineOperationalEvent({
    name: "dashboard.context.use_auth_bootstrap.session_check_failed.8c210aa7",
    message: "Session check failed:",
    level: "error",
    attributes: { "di.reason_code": { type: "category" } },
    classification: "operational",
    volume: "boundary",
    owner: "dashboard/src/context/useAuthBootstrap",
  }),
};
import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { identifyUser } from "../analytics";
import { message } from "../utils/notify";
import api, {
  buildAuthLogoutUrl,
  installAuthRecoveryInterceptor,
  installViewAsReadOnlyInterceptor,
  markForceLoginPromptAfterLogout,
  replaceBrowserLocation,
} from "../utils/api";
import type { AuthUser } from "../types";
import {
  AUTH_BOOTSTRAP_TIMEOUT_MS,
  authErrorMessage,
  getRequestConfig,
  type AuthBootstrapStage,
  type AuthRequestOptions,
} from "./authContextSupport";

export function useAuthSessionBootstrap({
  clearResolvedAuthError,
  clearResolvedAuthState,
  fetchUserSettings,
  isAuthenticated,
  setAuthError,
  setAuthErrorTitle,
  setIsAuthenticated,
  setIsLoading,
  setUser,
}: {
  clearResolvedAuthError: () => void;
  clearResolvedAuthState: () => void;
  fetchUserSettings: (options?: AuthRequestOptions) => Promise<void>;
  isAuthenticated: boolean;
  setAuthError: (message: string) => void;
  setAuthErrorTitle: (title: string) => void;
  setIsAuthenticated: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  setUser: Dispatch<SetStateAction<AuthUser | null>>;
}) {
  const clearResolvedAuthStateRef = useRef(clearResolvedAuthState);
  const fetchUserSettingsRef = useRef(fetchUserSettings);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    clearResolvedAuthStateRef.current = clearResolvedAuthState;
    fetchUserSettingsRef.current = fetchUserSettings;
    isAuthenticatedRef.current = isAuthenticated;
  }, [clearResolvedAuthState, fetchUserSettings, isAuthenticated]);

  useEffect(() => {
    return installAuthRecoveryInterceptor({
      clearAuthState: () => clearResolvedAuthStateRef.current(),
      isAuthenticated: () => isAuthenticatedRef.current,
      synchronizeAuthState: (options?: AuthRequestOptions) => fetchUserSettingsRef.current(options),
    });
  }, []);

  useEffect(() => {
    return installViewAsReadOnlyInterceptor({
      notify: (text: string) => message.warning(text, 3),
    });
  }, []);

  const setResolvedAuthError = useCallback(
    (stage: AuthBootstrapStage, error: unknown) => {
      const resolved = authErrorMessage(stage, error);
      setAuthErrorTitle(resolved.title);
      setAuthError(resolved.message);
    },
    [setAuthError, setAuthErrorTitle],
  );

  const checkSession = useCallback(async () => {
    try {
      clearResolvedAuthError();
      const response = await api.get(
        "/api/v2/auth/session",
        getRequestConfig({}, AUTH_BOOTSTRAP_TIMEOUT_MS),
      );
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        setUser(response.data.user);
        identifyUser(response.data.user);
        try {
          await fetchUserSettings({
            requestTimeoutMs: AUTH_BOOTSTRAP_TIMEOUT_MS,
          });
        } catch (error) {
          operationalLogger.error(operationalEvents.event1, error, {
            "di.reason_code": "profile_bootstrap_failed",
          });
          clearResolvedAuthState();
          setResolvedAuthError("profile", error);
        }
      } else {
        clearResolvedAuthState();
      }
    } catch (error) {
      operationalLogger.error(operationalEvents.event2, error, {
        "di.reason_code": "session_check_failed",
      });
      clearResolvedAuthState();
      setResolvedAuthError("session", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    clearResolvedAuthError,
    clearResolvedAuthState,
    fetchUserSettings,
    setIsAuthenticated,
    setIsLoading,
    setResolvedAuthError,
    setUser,
  ]);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  const handleLoginSuccess = useCallback(async () => {
    setIsAuthenticated(true);
    await fetchUserSettings();
  }, [fetchUserSettings, setIsAuthenticated]);

  const handleLogout = useCallback(async () => {
    // Keep route guards and Login from starting a competing navigation while
    // the browser completes the API and Keycloak logout redirects.
    setIsLoading(true);
    markForceLoginPromptAfterLogout();
    clearResolvedAuthState();
    replaceBrowserLocation(buildAuthLogoutUrl());
  }, [clearResolvedAuthState, setIsLoading]);

  return { handleLoginSuccess, handleLogout };
}
